import { Alert, Button, LinearProgress, Paper, TextField } from '@mui/material'
import { FormEvent, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import habitatLogo from '../../assets/habitat-logo.svg'
import styles from './login.module.css'
import {
    HA_SERVER_UNCONFIGURED_MESSAGE,
    resolveHaServerUrl,
} from '@utils/haServer'

const Login = () => {
    const [busy, setBusy] = useState(false)
    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')
    // Surfaced on load rather than on submit — there is no point
    // letting someone type their password into a form that cannot
    // reach anything.
    const [errorMessage, setErrorMessage] = useState<string | null>(
        resolveHaServerUrl() ? null : HA_SERVER_UNCONFIGURED_MESSAGE
    )
    const [, setSession] = useLocalStorage<string | null>('session', null)

    async function handleSubmit(event: FormEvent) {
        event.preventDefault()

        if (busy) return

        setErrorMessage(null)

        /**
         * Checked before the fields, not after. Nothing here can work
         * without it, so telling someone to fill the form in first
         * would replace the only message worth acting on with one that
         * changes nothing.
         */
        const haServerUrl = resolveHaServerUrl()

        if (!haServerUrl) {
            setErrorMessage(HA_SERVER_UNCONFIGURED_MESSAGE)
            return
        }

        if (!email.trim() || !password) {
            setErrorMessage('Enter your email address and password')
            return
        }

        setBusy(true)

        try {
            // Straight to Habitat Admin rather than through this
            // server — it owns staff sign-in, and there is no token
            // yet for the proxy to authorise the call with.
            const response = await fetch(`${haServerUrl}/v2/staff/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    email: email.trim().toLowerCase(),
                    password,
                }),
            })

            if (!response.ok) {
                const { name } = await response.json()

                /**
                 * Habitat Admin sends a name rather than a message so
                 * the wording lives here. Bad credentials stay vague
                 * on purpose — anything more specific would let
                 * someone work out which addresses belong to staff.
                 */
                if (name === 'StaffNoConsoleAccess') {
                    setErrorMessage(
                        'This account is not authorised to use the Staff Console'
                    )
                } else if (response.status === 401) {
                    setErrorMessage('Those details were not recognised')
                } else if (response.status === 429) {
                    /**
                     * Sign-in is rate limited, so a run of wrong
                     * passwords stops being answered for a while. Say
                     * that plainly and say how long — told only to
                     * wait, people retry immediately and stay locked
                     * out. `Retry-After` is in seconds.
                     */
                    const retryAfter = Number(
                        response.headers.get('Retry-After')
                    )
                    const wait =
                        Number.isFinite(retryAfter) && retryAfter > 0
                            ? `about ${Math.ceil(retryAfter / 60)} minute(s)`
                            : 'a minute'
                    setErrorMessage(
                        `Too many sign-in attempts. Wait ${wait} and try again.`
                    )
                } else {
                    setErrorMessage('Something went wrong')
                }

                return
            }

            const responseBody = await response.json()
            setSession(JSON.stringify(responseBody.result))
        } catch (error) {
            console.error(error)
            setErrorMessage('Something went wrong')
        } finally {
            setBusy(false)
        }
    }

    return (
        <div className={styles.screen}>
            <Paper className={styles.content} variant="outlined">
                {busy && <LinearProgress />}
                <div className={styles.inner}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '24px',
                        }}
                    >
                        <img
                            src={habitatLogo}
                            style={{
                                position: 'relative',
                                top: '4px',
                            }}
                        />
                        <h1>
                            <span>Habitat Learn</span>
                            <br />
                            Staff Console
                        </h1>
                    </div>
                    <form
                        onSubmit={handleSubmit}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            width: '278px',
                            margin: 'auto',
                        }}
                    >
                        <TextField
                            label="Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            autoComplete="username"
                            disabled={busy}
                            fullWidth
                        />
                        <TextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            autoComplete="current-password"
                            disabled={busy}
                            fullWidth
                        />
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={busy}
                        >
                            Sign in
                        </Button>
                    </form>
                    {errorMessage && (
                        <Alert className={styles.alert} severity="error">
                            {errorMessage}
                        </Alert>
                    )}
                </div>
            </Paper>
        </div>
    )
}

export default Login
